import orderBy from "lodash/orderBy";
import { useMemo } from "react";
import { useCustomers } from "../../customers/useCustomers";
import type { CustomerNotesFilters } from "../useAdminFilters";
import type { AdminCustomerNote } from "./customerNotesAdminApi";

type CustomerNoteWithResolvedAuthor = Omit<AdminCustomerNote, "author"> & {
  readonly author: { readonly id: string; readonly email: string; readonly name: string };
};

interface Params {
  readonly notes: CustomerNoteWithResolvedAuthor[];
  readonly filters: Pick<CustomerNotesFilters, "accountManagerId" | "authorId" | "type">;
}

export function useFilteredNotes({ notes, filters }: Params) {
  const { customers, isLoading: isCustomersLoading } = useCustomers();
  const { accountManagerId, authorId, type } = filters;

  const accountManagerIdByCustomerId = useMemo(() => {
    return new Map(customers.map((c) => [c.id, c.accountManager?.id ?? null]));
  }, [customers]);

  const filteredNotes = useMemo(() => {
    const sortedNotes = orderBy(notes, (n) => n.date, ["desc"]);
    return sortedNotes
      .filter((n) => !type || n.type === type)
      .filter((n) => !authorId || n.author.id === authorId)
      .filter((n) => !accountManagerId || accountManagerIdByCustomerId.get(n.customer.id) === accountManagerId);
  }, [accountManagerId, accountManagerIdByCustomerId, authorId, isCustomersLoading, notes, type]);

  const isLoading = isCustomersLoading && !!accountManagerId;
  return { filteredNotes, isLoading };
}
